import { FC, useEffect } from 'react'
import cn from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import {
    UseFormRegister,
    Control,
    useWatch,
    FieldErrors,
    UseFormUnregister
} from 'react-hook-form'
import type { GiftCardProductInputArgs } from './GiftCardProduct'
import type { GiftCardProduct } from '@pylot-data/pylotschema'
import { OtherAmountEnum } from './AmountDropdown'
import s from '@pagestyles/Giftcard.module.scss'

export type Props = {
    register: UseFormRegister<GiftCardProductInputArgs>
    control: Control<GiftCardProductInputArgs>
    errors: FieldErrors<GiftCardProductInputArgs>
    unregister: UseFormUnregister<GiftCardProductInputArgs>
    product: GiftCardProduct
}

export const OpenAmount: FC<Props> = ({
    register,
    control,
    errors,
    unregister,
    product
}) => {
    const { t } = useTranslation(['pdp'])
    const {
        allow_open_amount,
        giftcard_amounts,
        open_amount_min,
        open_amount_max
    } = product

    const amount = useWatch({
        control,
        name: 'gift_amounts',
        defaultValue: allow_open_amount ? OtherAmountEnum.Other : ''
    })

    useEffect(() => {
        if (amount !== OtherAmountEnum.Other) {
            unregister('gift_open_amounts')
        }
    }, [amount])

    const getOpenAmountErrors = () => {
        if (errors.gift_open_amounts?.message) {
            return t('pdp||giftCard|{{ fieldError }}', {
                fieldError: errors.gift_open_amounts?.message
            })
        }

        if (errors?.gift_open_amounts?.type === 'min') {
            return t(
                'pdp||giftCard|Please enter a value greater than or equal to {{ min }}.',
                {
                    min: open_amount_min
                }
            )
        }

        if (errors?.gift_open_amounts?.type === 'max') {
            return t(
                'pdp||giftCard|Please enter a value less than or equal to {{ max }}.',
                {
                    max: open_amount_max
                }
            )
        }

        return ''
    }

    if (
        !allow_open_amount ||
        (amount !== OtherAmountEnum.Other && giftcard_amounts?.length)
    ) {
        return null
    }

    return (
        <div className="open-amount-container">
            <div className="form-group">
                <label className="heading" htmlFor="gift_open_amounts">
                    {t('pdp||giftCard|Amount in USD')}
                    <span className="asterisk">*</span>
                </label>
                <input
                    type="text"
                    {...register('gift_open_amounts', {
                        required: 'This is a required field',
                        valueAsNumber: true,
                        validate: (value) =>
                            !isNaN(Number(value)) ||
                            'Please enter a valid number',
                        min: open_amount_min!,
                        max: open_amount_max!
                    })}
                    className={cn('form-input', {
                        'border-alert-danger': errors.gift_open_amounts
                    })}
                    id="gift_open_amounts"
                />
                {errors.gift_open_amounts && (
                    <span className="error">{getOpenAmountErrors()}</span>
                )}
                {/* todo: work on currency converter */}
                <div className={s['open-amount-note']}>
                    {t(
                        'pdp||giftCard|Minimum: ${{ min }} Maximum: $ {{ max }}',
                        {
                            min: open_amount_min,
                            max: open_amount_max
                        }
                    )}
                </div>
            </div>
        </div>
    )
}
